'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef } from 'react';
import { annotate } from 'rough-notation';
import { useMountEffect } from '@/hooks/useMountEffect';

// A handwritten margin note beside a paragraph. On wide screens it sits in the
// gutter and a bracket draws in once, the first time the paragraph scrolls
// into view; under 1180px the note sits under the paragraph and story.css
// hides the bracket. The bracket's colour comes from CSS (currentColor), so a
// theme switch recolours it without a redraw. Keep these rare: two a page.
export function Note(t0) {
  const $ = _c(10);
  const {
    note,
    top: t1,
    bracket: t2,
    tilt,
    children
  } = t0;
  const top = t1 === undefined ? false : t1;
  const bracket = t2 === undefined ? true : t2;
  const ref = useRef(null);
  const bracketRef = useRef(bracket);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const target = ref.current?.querySelector("p");
      if (!bracketRef.current || !target) {
        return;
      }
      let annotation = null;
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(_temp)) {
          return;
        }
        observer.disconnect();
        annotation = annotate(target, {
          type: "bracket",
          brackets: ["right"],
          strokeWidth: 1,
          padding: [2, 14],
          animationDuration: 700
        });
        annotation.show();
      }, {
        rootMargin: "0px 0px -20% 0px"
      });
      observer.observe(target);
      return () => {
        observer.disconnect();
        annotation?.remove();
      };
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  useMountEffect(t3);
  const t4 = top || undefined;
  let t5;
  if ($[1] !== tilt) {
    t5 = tilt === undefined ? undefined : {
      "--tilt": `${tilt}deg`
    } as React.CSSProperties;
    $[1] = tilt;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== note || $[4] !== t4 || $[5] !== t5) {
    t6 = <span className="story-note" data-top={t4} style={t5}>{note}</span>;
    $[3] = note;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== children || $[8] !== t6) {
    t7 = <div ref={ref} className="story-annotated">{children}{t6}</div>;
    $[7] = children;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function _temp(e) {
  return e.isIntersecting;
}